import { useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useAuth } from "@/hooks/useAuth";
import OrgSwitcher from "@/components/OrgSwitcher";
import { getAllSections } from "@/lib/dashboardNav";
import clsx from "clsx";

// Public nav items for public pages (home, pricing, status)
const publicNavItems = [
  { href: "/", label: "Home", icon: "fa-solid fa-house" },
  { href: "/pricing", label: "Pricing", icon: "fa-solid fa-tag" },
  { href: "/docs", label: "Documentation", icon: "fa-solid fa-book" },
  { href: "/blog", label: "Blog", icon: "fa-solid fa-newspaper" },
  { href: "/claims-calculator", label: "Claims Calculator", icon: "fa-solid fa-calculator" },
  { href: "/status", label: "Status", icon: "fa-solid fa-signal" },
];

// Site links shown at the bottom of dashboard and docs mobile navs
const siteNavLinks = [
  { href: "/", label: "Home", icon: "fa-solid fa-house" },
  { href: "/pricing", label: "Pricing", icon: "fa-solid fa-tag" },
  { href: "/docs", label: "Documentation", icon: "fa-solid fa-book" },
  { href: "/blog", label: "Blog", icon: "fa-solid fa-newspaper" },
  { href: "/claims-calculator", label: "Claims Calculator", icon: "fa-solid fa-calculator" },
  { href: "/status", label: "Status", icon: "fa-solid fa-signal" },
];

interface MobileNavProps {
  variant?: "dashboard" | "docs" | "public";
  docsNavigation?: { sections: { title: string; items: { title: string; href: string; badge?: string }[] }[] };
}

export default function MobileNav({ variant = "dashboard", docsNavigation }: MobileNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  const allSections = getAllSections(!!user?.is_superuser);

  const isActive = (href: string) => {
    if (href === "/" || href === "/dashboard") {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Hamburger button - only visible on mobile */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden flex items-center justify-center w-10 h-10 rounded-m text-text hover:bg-surface-raised transition-colors"
        aria-label="Open navigation menu"
      >
        <i className="fa-solid fa-bars text-lg" />
      </button>

      {/* Mobile drawer */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={() => setIsOpen(false)} className="relative z-50 md:hidden">
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          </TransitionChild>

          {/* Drawer panel */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 w-72 bg-surface border-r border-neutral-subtle shadow-xl flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-neutral-subtle">
                <span className="font-semibold text-text">
                  {variant === "docs" ? "Documentation" : variant === "public" ? "Navigation" : "Menu"}
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-m text-text-quiet hover:bg-surface-raised transition-colors"
                  aria-label="Close menu"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>

              {variant === "dashboard" && (
                <>
                  {/* Org Switcher */}
                  <div className="p-3 border-b border-neutral-subtle">
                    <OrgSwitcher />
                  </div>

                  {/* Navigation */}
                  <nav className="flex-1 overflow-y-auto p-3">
                    <div className="space-y-6">
                      {allSections.map((section, index) => (
                        <div key={section.title} className={clsx(index > 0 && "mt-3")}>
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-text-quiet pb-1 px-3">
                            {section.title}
                          </div>
                          <div className="space-y-0.5">
                            {section.items.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href}
                                onClick={() => setIsOpen(false)}
                                className={clsx(
                                  "flex items-center gap-3 px-3 py-2.5 rounded-m text-sm transition-colors",
                                  isActive(item.href)
                                    ? "bg-brand-subtle text-brand-500 font-semibold"
                                    : "text-text hover:bg-surface-raised"
                                )}
                              >
                                <i className={item.icon} />
                                <span>{item.label}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </nav>

                  {/* Footer links — site navigation + sign out */}
                  <div className="p-3 border-t border-neutral-subtle space-y-1">
                    {siteNavLinks.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-m text-sm text-text-quiet hover:bg-surface-raised transition-colors"
                      >
                        <i className={clsx(item.icon, "w-5 text-center")} />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        logout();
                      }}
                      className="flex items-center gap-3 px-3 py-2 rounded-m text-sm text-text hover:bg-surface-raised transition-colors w-full"
                    >
                      <i className="fa-solid fa-right-from-bracket w-5 text-center" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </>
              )}

              {variant === "docs" && docsNavigation && (
                <>
                  <nav className="flex-1 overflow-y-auto p-3">
                    <div className="space-y-6">
                      {docsNavigation.sections.map((section, index) => (
                        <div key={section.title} className={clsx(index > 0 && "mt-3")}>
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-text-quiet pb-1 px-3">
                            {section.title}
                          </div>
                          <div className="space-y-0.5">
                            {section.items.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href}
                                onClick={() => setIsOpen(false)}
                                className={clsx(
                                  "flex items-center gap-2 px-3 py-2.5 rounded-m text-sm transition-colors",
                                  pathname === item.href
                                    ? "bg-brand-subtle text-brand-500 font-semibold"
                                    : "text-text hover:bg-surface-raised"
                                )}
                              >
                                <span>{item.title}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </nav>

                  {/* Site navigation links */}
                  <div className="p-3 border-t border-neutral-subtle space-y-1">
                    {siteNavLinks.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-m text-sm text-text-quiet hover:bg-surface-raised transition-colors"
                      >
                        <i className={clsx(item.icon, "w-5 text-center")} />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {variant === "public" && (
                <>
                  {/* Public navigation */}
                  <nav className="flex-1 overflow-y-auto p-3">
                    <div className="space-y-0.5">
                      {publicNavItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={clsx(
                            "flex items-center gap-3 px-3 py-2.5 rounded-m text-sm transition-colors",
                            isActive(item.href)
                              ? "bg-brand-subtle text-brand-500 font-semibold"
                              : "text-text hover:bg-surface-raised"
                          )}
                        >
                          <i className={clsx(item.icon, "w-5 text-center")} />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </nav>

                  {/* Dashboard CTA */}
                  <div className="p-3 border-t border-neutral-subtle">
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                      style={{ backgroundColor: "var(--color-brand-600)", color: "#ffffff" }}
                    >
                      <i className="fa-solid fa-arrow-right-to-bracket" />
                      <span>Go to Dashboard</span>
                    </Link>
                  </div>
                </>
              )}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
}
